/**
 * Kapselkomponemnte für Messwerteingabe der Kalibrierung eines 4-Gas Abgastesters
 */

import { observer } from 'mobx-react';
import { computed, action } from 'mobx';
import * as React from 'react';
import { inject } from '../../../utils/decorators';
import { Agt4GasKalibrierungStore } from '../../../stores/Agt4GasKalibrierungStore';
import { UiStore } from '../../../stores/UiStore';
import Agt4GasMessung, { PruefgasArt } from './Agt4GasMessung';
import Callout from '../../site/Callout';
import VorgangFormularElement from '../VorgangFormularElement';
import AgtPruefortParameter from './AgtPruefortParameter';
import { Checkbox, FormControl } from 'react-bootstrap';
import { bind } from '../../../utils/decorators';
import { ApiStore } from '../../../stores/ApiStore';
import { IObjekt, AGT_4GAS, AGT_KOMBI } from '../../../models/ApiTypes';

export interface IAgt4GaskalibrierungProps {
  elementguid: string;
  kategorieguid: string;
  editable?: boolean;
  value?: string;
  element: VorgangFormularElement;
}

@observer
export default class Agt4GasKalibrierung extends React.Component<IAgt4GaskalibrierungProps, {}> {

    @inject(UiStore) uiStore: UiStore;
    @inject(ApiStore) apiStore: ApiStore;
    @inject(Agt4GasKalibrierungStore) kalibrierungStore: Agt4GasKalibrierungStore;

    abgastester: IObjekt | undefined;

    constructor(props: IAgt4GaskalibrierungProps) {
      super(props);

      // Datenübernahme aus dem im Auftrag gesetzten Opazimeter
      const kalGegenstand = this.kalibrierungStore.getKalibrierGegenstand();
      if (kalGegenstand && (kalGegenstand.art === AGT_4GAS || kalGegenstand.art === AGT_KOMBI)) {
        this.abgastester = kalGegenstand;
      }

      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        // Gespeicherte Optionen
        if (obj.optionen) {
          if (obj.optionen.justage) {
            this.kalibrierungStore.setJustage(obj.optionen.justage === 'on');
          }
          if (obj.optionen.messunsicherheitanwenden) {
            this.kalibrierungStore.setMessunsicherheitAnwenden(obj.optionen.messunsicherheitanwenden === 'on');
          }
          if (obj.optionen.genauigkeitsklasseueberschreiben) {
            this.kalibrierungStore.setGenauigkeitsklasseUeberschreiben(obj.optionen.genauigkeitsklasseueberschreiben === 'on');
          }
        }
      }

      this.kalibrierungStore.setReadOnly(this.props.editable ? !this.props.editable : true);
    }

    async componentDidMount() {
      const kalibrierGegenstand = this.kalibrierungStore.getKalibrierGegenstand();
      if (kalibrierGegenstand) {
        const objektVorlage = await this.apiStore.objektVorlage(kalibrierGegenstand.objektguid);
        if (objektVorlage.ok) {
          this.kalibrierungStore.setObjektVorlage(objektVorlage.json);
        }
      }
    }

    /** Liefert die im AGT (als Objekt des Auftrags) hinterlegte Genauigkeitsklasse zurück */
    @computed get genauigkeitsklasseAGTGeraet() {
      const kalibrierGegenstand = this.kalibrierungStore.getKalibrierGegenstand();
      if (kalibrierGegenstand) {
        if (kalibrierGegenstand && kalibrierGegenstand.meta) {
          let fehlergrenze = kalibrierGegenstand.meta.find((meta) => meta.bezeichnung === 'agt_genauigkeitsklasse');
          if (fehlergrenze) {
            return fehlergrenze.inhalt;
          }
        }
      }
      // Wenn keine Genauigkeitsklasse hinterlegt wurde (Objekt z.B. im QIT angelegt oder nachträglich verändert) maximale Genauigkeitsklasse zurückgeben
      return '00';
    }

    /** Die aktuelle Richtlinie erlaubt es, Geräte mit Genauigkeitsklasse 1 wie solche mit Klasse 0 zu behandeln, dies muss dann mit einbezogen werden */
    @computed get genauigkeitsklasseAGT() {
      const genauigkeitsKlasseBasis = this.genauigkeitsklasseAGTGeraet;
      if (genauigkeitsKlasseBasis === '1' && this.kalibrierungStore.genauigkeitsklasseUeberschreiben) {
        return '0';
      }
      return genauigkeitsKlasseBasis;
    }

    /**
     * JSON-Key Prefix für an Back-End übergebene
     */
    @computed get formdataprefix() {
      return `${this.props.kategorieguid}[${this.props.elementguid}]`;
    }

    @bind @action handleMessunsicherheitAnwendenChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setMessunsicherheitAnwenden((ev.target as HTMLInputElement).checked);
    }

    @bind @action handleJustageChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setJustage((ev.target as HTMLInputElement).checked);
    }

    @bind @action handleGenauigkeitsklasseUeberschreibenChange(ev: React.FormEvent<FormControl>) {
      this.kalibrierungStore.setGenauigkeitsklasseUeberschreiben((ev.target as HTMLInputElement).checked);
    }

    public renderMessReihe(nachJustage: boolean) {
      // Wenn bereits Werte erfasst wurden, in jeweiligen Komponenten übernehmen
      let messungen = undefined;
      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        if (nachJustage) {
          if (obj.nachjustage) {
            messungen = obj.nachjustage.messungen;
          }
        } else {
          messungen = obj.messungen;
        }
      }
      let prefix = this.formdataprefix;
      if (nachJustage) {
        prefix = prefix + '[nachjustage]';
      }
      return (
        <div>
          <Agt4GasMessung
            pruefgasArt={PruefgasArt.O2}
            genauigkeitsklasse={this.genauigkeitsklasseAGT}
            formdataprefix={`${prefix}[messungen][0]`}
            value={messungen && messungen[0] ? JSON.stringify(messungen[0]) : undefined}
            nachjustage={nachJustage}
          />
          <Agt4GasMessung
            pruefgasArt={PruefgasArt.A}
            genauigkeitsklasse={this.genauigkeitsklasseAGT}
            formdataprefix={`${prefix}[messungen][1]`}
            value={messungen && messungen[1] ? JSON.stringify(messungen[1]) : undefined}
            nachjustage={nachJustage}
          />
          <Agt4GasMessung
            // Die für die zweite Messung zu verwendende Prüfgasart hängt von der Genauigkeitsklasse des AGTs ab
            // Bei 1 Prüfgas B
            // Bei 0 und 00 Prüfgas C
            pruefgasArt={this.genauigkeitsklasseAGT === '1' ? PruefgasArt.B : PruefgasArt.C}
            genauigkeitsklasse={this.genauigkeitsklasseAGT}
            formdataprefix={`${prefix}[messungen][2]`}
            value={messungen && messungen[2] ? JSON.stringify(messungen[2]) : undefined}
            nachjustage={nachJustage}
          />
        </div>
      );
    }

    public render() {
      // Wenn bereits Werte erfasst wurden, in jeweiligen Komponenten übernehmen
      let parameter = undefined;
      if (this.props.value) {
        var obj = JSON.parse(this.props.value);
        if (obj.parameter) {
          parameter = obj.parameter;
        }
      }

      return(
        <div>
          {this.abgastester !== undefined ?
            <div>
              {/* Interne Nutzer können wählen ob die Messunsicherheit auf den Toleranzbereich angewendet werden soll */}
              {this.uiStore.userProfile && this.uiStore.userProfile.portaladmin &&
                <Callout bsStyle="warning">
                  <Checkbox
                        inline={true}
                        id="checkbox_mu_anwenden"
                        name={`${this.formdataprefix}[optionen][messunsicherheitanwenden]`}
                        disabled={!this.props.editable}
                        onChange={this.props.editable ? this.handleMessunsicherheitAnwendenChange : undefined}
                        defaultChecked={this.kalibrierungStore.messunsicherheitAnwenden}
                      >
                        Messunsicherheit für Berechnung Toleranzbereich nutzen
                  </Checkbox>
                  <div className="text-hint-light">
                    <b>Hinweis:</b> Diese Auswahl steht nur internen Nutzern zur Verfügung.
                  </div>
                </Callout>
              }

              <AgtPruefortParameter
                titel="Prüfortparameter zu Beginn der Kalibrierung"
                formdataprefix={`${this.formdataprefix}[parameter][0]`}
                bereichLuftfeuchtigkeit={{min: 0, max: 85}}
                bereichUmgebungsdruck={{min: 860, max: 1060}}
                bereichLufttemperatur={{min: 10, max: 30}}
                editable={this.props.editable}
                value={parameter && parameter[0] ? JSON.stringify(parameter[0]) : undefined}
              />

              {/* Nach aktueller Kalibrierrichtlinie müssen sich Geräte mit Genauigkeitsklasse 1 optional wie Geräte mit Genauigkeitsklasse 0 behandeln lassen */}
              {this.genauigkeitsklasseAGTGeraet === '1' &&
                <Callout bsStyle="warning">
                  <Checkbox
                    inline={true}
                    id="checkbox_genauigkeitsklasseueberschreiben"
                    name={`${this.formdataprefix}[optionen][genauigkeitsklasseueberschreiben]`}
                    disabled={!this.props.editable}
                    onChange={this.props.editable ? this.handleGenauigkeitsklasseUeberschreibenChange : undefined}
                    defaultChecked={this.kalibrierungStore.genauigkeitsklasseUeberschreiben}
                  >
                    Wie Gerät mit Genauigkeitsklasse 0 behandeln
                  </Checkbox>
                  <div className="text-hint-light">
                      Das Gerät wird wie ein Gerät der Genauigkeitsklasse 0 behandelt.
                  </div>
                </Callout>
              }

              {this.renderMessReihe(false)}

              {/* Optionale Eingabe nach Justage (wenn notwendig) */}
              <Callout bsStyle="default">
                <Checkbox
                  inline={true}
                  id="checkbox_justage"
                  name={`${this.formdataprefix}[optionen][justage]`}
                  disabled={!this.props.editable}
                  onChange={this.props.editable ? this.handleJustageChange : undefined}
                  defaultChecked={this.kalibrierungStore.justage}
                >
                  Justage durchgeführt
                </Checkbox>
                <div className="text-hint-light">
                    Zeigt eine zweite Messwerteingabe für die Erfassung der Daten nach Durchführung einer Justage an. Beim Erzeugen des Kalibrierscheins werden diese Messwerte übernommen.
                </div>
              </Callout>

              {this.kalibrierungStore.justage &&
                  this.renderMessReihe(true)
              }

              <AgtPruefortParameter
                titel="Prüfortparameter am Ende der Kalibrierung"
                formdataprefix={`${this.formdataprefix}[parameter][1]`}
                bereichLuftfeuchtigkeit={{min: 0, max: 85}}
                bereichUmgebungsdruck={{min: 860, max: 1060}}
                bereichLufttemperatur={{min: 10, max: 30}}
                editable={this.props.editable}
                value={parameter && parameter[1] ? JSON.stringify(parameter[1]) : undefined}
              />
            </div>
            :
            <Callout bsStyle="danger">
              <p>Für die Erstellung der Auswertung muss ein 4-Gas Tester oder Kombigerät ausgewählt werden.</p>
            </Callout>
          }
        </div>
      );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/agt/Agt4GasKalibrierung.tsx